/**
 * SelfOrderSettings Vuex Module
 * セルフオーダーのカスタマイズ設定を管理する
 */
import SelfOrderSettingApi from '@/api/admin/SelfOrderSetting'

// アイコンオプション定義（バックエンドと同期）
export const ICON_OPTIONS = [
  { value: 'default', text: 'デフォルト', icon: 'mdi-tag' },
  { value: 'home', text: 'ホーム', icon: 'mdi-home' },
  { value: 'menu', text: 'メニュー', icon: 'mdi-menu' },
  { value: 'beer', text: 'ビール', icon: 'mdi-glass-mug-variant' },
  { value: 'wine', text: 'ワイン', icon: 'mdi-glass-wine' },
  { value: 'cocktail', text: 'カクテル', icon: 'mdi-glass-cocktail' },
  { value: 'drink', text: 'ドリンク', icon: 'mdi-cup' },
  { value: 'bottle', text: 'ボトル', icon: 'mdi-bottle-wine' },
  { value: 'champagne', text: 'シャンパン', icon: 'mdi-bottle-wine-outline' },
  { value: 'utensils', text: 'フード', icon: 'mdi-silverware-fork-knife' },
  { value: 'food', text: '食事', icon: 'mdi-food' },
  { value: 'cake', text: 'ケーキ', icon: 'mdi-cake-variant' },
  { value: 'sparkles', text: 'キラキラ', icon: 'mdi-shimmer' },
  { value: 'star', text: 'おすすめ', icon: 'mdi-star' },
  { value: 'heart', text: 'お気に入り', icon: 'mdi-heart' },
  { value: 'gift', text: 'ギフト', icon: 'mdi-gift' },
  { value: 'flower', text: '花', icon: 'mdi-flower' },
  { value: 'camera', text: 'チェキ', icon: 'mdi-camera' },
  { value: 'music', text: '音楽', icon: 'mdi-music' },
  { value: 'settings', text: '設定', icon: 'mdi-cog' },
]

// デフォルトのテーマ色
export const DEFAULT_THEME = {
  primaryColor: '#d4bc83',
  backgroundColor: '#1a1826',
  cardColor: '#232030',
  textColor: '#ffffff',
}

/**
 * Active Storage画像用のベースURLを取得
 * VUE_APP_API_ENDPOINT_BASEから/apiを除去したURLを返す
 */
const getAssetBaseUrl = () => {
  const apiBase = process.env.VUE_APP_API_ENDPOINT_BASE || ''

  // /api を末尾から除去
  return apiBase.replace(/\/api$/, '')
}

/**
 * 画像URLを解決する。CDN経由の絶対URLはそのまま、相対パスはベースURLを付与
 */
const resolveAssetUrl = url => {
  if (!url) return null
  if (url.startsWith('http://') || url.startsWith('https://')) return url

  return `${getAssetBaseUrl()}${url}`
}

export default {
  namespaced: true,

  state: {
    // 設定データ
    setting: null,
    categories: [],
    availableTags: [],
    categoryItems: {}, // { vendibleTagId: [items] }

    // ローディング状態
    loading: false,
    saving: false,

    // エラー状態
    error: null,
  },

  getters: {
    /**
     * 設定が読み込み済みかどうか
     */
    isLoaded: state => state.setting !== null,

    /**
     * 現在のテーマ設定
     */
    theme: state => {
      if (!state.setting) return DEFAULT_THEME

      return {
        primaryColor: state.setting.primaryColor || DEFAULT_THEME.primaryColor,
        backgroundColor: state.setting.backgroundColor || DEFAULT_THEME.backgroundColor,
        cardColor: state.setting.cardColor || DEFAULT_THEME.cardColor,
        textColor: state.setting.textColor || DEFAULT_THEME.textColor,
      }
    },

    /**
     * 機能フラグ（指名・コース有効）
     */
    featureFlags: state => ({
      nominationEnabled: state.setting?.nominationEnabled ?? false,
      courseEnabled: state.setting?.courseEnabled ?? false,
    }),

    /**
     * 画像URL（アセットベースURLを付与）
     */
    logoUrl: state => resolveAssetUrl(state.setting?.logoUrl),
    bannerUrl: state => resolveAssetUrl(state.setting?.bannerUrl),

    /**
     * カテゴリ一覧（位置順）
     */
    sortedCategories: state => [...state.categories].sort((a, b) => a.position - b.position),

    /**
     * 未選択のタグ一覧
     */
    unselectedTags: state => {
      const selectedIds = state.categories.map(c => c.vendibleTagId)

      return state.availableTags.filter(tag => !selectedIds.includes(tag.id))
    },

    /**
     * アイコンオプション
     */
    iconOptions: () => ICON_OPTIONS,

    /**
     * カテゴリの商品一覧を取得
     */
    getCategoryItems: state => vendibleTagId => state.categoryItems[vendibleTagId] || [],
  },

  mutations: {
    SET_LOADING(state, value) {
      state.loading = value
    },

    SET_SAVING(state, value) {
      state.saving = value
    },

    SET_ERROR(state, error) {
      state.error = error
    },

    SET_SETTING(state, setting) {
      state.setting = setting
    },

    SET_CATEGORIES(state, categories) {
      state.categories = categories || []
    },

    SET_AVAILABLE_TAGS(state, tags) {
      state.availableTags = tags || []
    },

    /**
     * カテゴリを追加
     */
    ADD_CATEGORY(state, { vendibleTagId, vendibleTagName, icon = 'default' }) {
      const position = state.categories.length
      state.categories.push({
        vendibleTagId,
        vendibleTagName,
        icon,
        position,
      })
    },

    /**
     * カテゴリを削除
     */
    REMOVE_CATEGORY(state, vendibleTagId) {
      state.categories = state.categories
        .filter(c => c.vendibleTagId !== vendibleTagId)
        .map((cat, index) => ({ ...cat, position: index }))
    },

    /**
     * カテゴリのアイコンを更新
     */
    UPDATE_CATEGORY_ICON(state, { vendibleTagId, icon }) {
      const category = state.categories.find(c => c.vendibleTagId === vendibleTagId)
      if (category) {
        category.icon = icon
      }
    },

    /**
     * カテゴリの並び順を更新
     */
    REORDER_CATEGORIES(state, categories) {
      state.categories = categories.map((cat, index) => ({
        ...cat,
        position: index,
      }))
    },

    /**
     * テーマ色を更新（プレビュー用）
     */
    UPDATE_THEME_COLOR(state, { field, value }) {
      if (state.setting) {
        state.setting[field] = value
      }
    },

    /**
     * 機能フラグ（指名・コース有効）を更新
     */
    UPDATE_FEATURE_FLAG(state, { field, value }) {
      if (state.setting) {
        state.setting[field] = value
      }
    },

    /**
     * カテゴリの商品一覧を設定
     */
    SET_CATEGORY_ITEMS(state, { vendibleTagId, items }) {
      state.categoryItems = {
        ...state.categoryItems,
        [vendibleTagId]: items,
      }
    },

    /**
     * 商品の画像URLを更新
     */
    UPDATE_ITEM_IMAGE(state, { itemId, imageUrl }) {
      Object.keys(state.categoryItems).forEach(tagId => {
        const items = state.categoryItems[tagId]
        const item = items.find(i => i.id === itemId)
        if (item) {
          item.imageUrl = imageUrl
        }
      })
    },
  },

  actions: {
    /**
     * 設定を取得
     */
    async fetchSetting({ commit }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)

      try {
        const response = await SelfOrderSettingApi.getSelfOrderSetting()
        const { data } = response.data.selfOrderSetting || {}

        if (data) {
          commit('SET_SETTING', data.attributes)
          commit('SET_CATEGORIES', data.attributes.categories || [])
        } else {
          // 設定が存在しない場合はデフォルト値を設定
          commit('SET_SETTING', { ...DEFAULT_THEME, nominationEnabled: false, courseEnabled: false })
          commit('SET_CATEGORIES', [])
        }
      } catch (error) {
        console.error('[SelfOrderSettings] Failed to fetch setting:', error)
        commit('SET_ERROR', error.response?.data?.errors || ['設定の取得に失敗しました'])
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * 利用可能なVendibleTagを取得
     */
    async fetchVendibleTags({ commit }) {
      try {
        const response = await SelfOrderSettingApi.getVendibleTags()
        commit('SET_AVAILABLE_TAGS', response.data.vendibleTags || [])
      } catch (error) {
        console.error('[SelfOrderSettings] Failed to fetch vendible tags:', error)
        throw error
      }
    },

    /**
     * 設定を保存
     */
    async saveSetting({ commit, state }, {
      logo, banner, removeLogo, removeBanner,
    } = {}) {
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)

      try {
        const response = await SelfOrderSettingApi.updateSelfOrderSetting({
          primaryColor: state.setting.primaryColor,
          backgroundColor: state.setting.backgroundColor,
          cardColor: state.setting.cardColor,
          textColor: state.setting.textColor,
          nominationEnabled: state.setting.nominationEnabled ?? false,
          courseEnabled: state.setting.courseEnabled ?? false,
          logo,
          banner,
          removeLogo,
          removeBanner,
        })

        const { data } = response.data.selfOrderSetting || {}
        if (data) {
          commit('SET_SETTING', data.attributes)
          commit('SET_CATEGORIES', data.attributes.categories || [])
        }

        return response
      } catch (error) {
        console.error('[SelfOrderSettings] Failed to save setting:', error)
        commit('SET_ERROR', error.response?.data?.errors || ['設定の保存に失敗しました'])
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    /**
     * カテゴリを追加
     */
    async addCategory({ commit, state }, vendibleTagId) {
      const tag = state.availableTags.find(t => t.id === vendibleTagId)
      if (!tag) return null

      commit('SET_SAVING', true)
      commit('SET_ERROR', null)

      try {
        const response = await SelfOrderSettingApi.addCategory(vendibleTagId, 'default')
        const { category } = response.data

        commit('ADD_CATEGORY', {
          vendibleTagId: category.vendibleTagId,
          vendibleTagName: category.vendibleTagName,
          icon: category.icon,
          position: category.position,
        })

        return response
      } catch (error) {
        console.error('[SelfOrderSettings] Failed to add category:', error)
        commit('SET_ERROR', error.response?.data?.errors || ['カテゴリの追加に失敗しました'])
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    /**
     * カテゴリを削除
     */
    async removeCategory({ commit }, vendibleTagId) {
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)

      try {
        await SelfOrderSettingApi.removeCategory(vendibleTagId)
        commit('REMOVE_CATEGORY', vendibleTagId)
      } catch (error) {
        console.error('[SelfOrderSettings] Failed to remove category:', error)
        commit('SET_ERROR', error.response?.data?.errors || ['カテゴリの削除に失敗しました'])
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    /**
     * カテゴリのアイコンを更新
     */
    async updateCategoryIcon({ commit }, { vendibleTagId, icon }) {
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)

      try {
        await SelfOrderSettingApi.updateCategoryIcon(vendibleTagId, icon)
        commit('UPDATE_CATEGORY_ICON', { vendibleTagId, icon })
      } catch (error) {
        console.error('[SelfOrderSettings] Failed to update category icon:', error)
        commit('SET_ERROR', error.response?.data?.errors || ['アイコンの更新に失敗しました'])
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    /**
     * カテゴリの並び順を更新
     */
    async reorderCategories({ commit, state }, categories) {
      commit('REORDER_CATEGORIES', categories)

      commit('SET_SAVING', true)
      commit('SET_ERROR', null)

      try {
        const categoryIds = categories.map(c => c.vendibleTagId)
        await SelfOrderSettingApi.reorderCategories(categoryIds)
      } catch (error) {
        console.error('[SelfOrderSettings] Failed to reorder categories:', error)
        commit('SET_ERROR', error.response?.data?.errors || ['並び順の保存に失敗しました'])
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    /**
     * テーマ色を更新（プレビュー用）
     */
    updateThemeColor({ commit }, { field, value }) {
      commit('UPDATE_THEME_COLOR', { field, value })
    },

    /**
     * 機能フラグ（指名・コース有効）を更新
     */
    updateFeatureFlag({ commit }, { field, value }) {
      commit('UPDATE_FEATURE_FLAG', { field, value })
    },

    /**
     * カテゴリの商品一覧を取得
     */
    async fetchCategoryItems({ commit }, vendibleTagId) {
      try {
        const response = await SelfOrderSettingApi.getCategoryItems(vendibleTagId)
        // 画像URLを解決（相対パスのみベースURLを付与）
        const items = (response.data.items || []).map(item => ({
          ...item,
          imageUrl: resolveAssetUrl(item.imageUrl),
        }))

        commit('SET_CATEGORY_ITEMS', {
          vendibleTagId,
          items,
        })

        return items
      } catch (error) {
        console.error('[SelfOrderSettings] Failed to fetch category items:', error)
        throw error
      }
    },

    /**
     * 商品画像を更新
     */
    async updateItemImage({ commit }, { itemId, image }) {
      try {
        const response = await SelfOrderSettingApi.updateItemImage(itemId, image)
        const imageUrl = resolveAssetUrl(response.data.imageUrl)

        commit('UPDATE_ITEM_IMAGE', {
          itemId,
          imageUrl,
        })

        return response.data
      } catch (error) {
        console.error('[SelfOrderSettings] Failed to update item image:', error)
        throw error
      }
    },

    /**
     * 商品画像を削除
     */
    async removeItemImage({ commit }, itemId) {
      try {
        const response = await SelfOrderSettingApi.removeItemImage(itemId)
        commit('UPDATE_ITEM_IMAGE', {
          itemId,
          imageUrl: null,
        })

        return response.data
      } catch (error) {
        console.error('[SelfOrderSettings] Failed to remove item image:', error)
        throw error
      }
    },
  },
}
